import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  currentAddressSchema,
  type CurrentAddressFormValues,
} from '~domains/profile/entities/person';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen } from '~shared/ui/screen';

import { TextField } from '@breezeventures/ui-kit/text-field/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';
import { AddressSuggestions } from '@breezeventures/web-ph-address-suggestions/address-suggestions';
import { zodResolver } from '@hookform/resolvers/zod';

import { PersonFormHeader } from '../../../person-form-header';

const suggestionFieldNames = {
  country: 'country',
  region: 'region',
  province: 'province',
  cityOrMunicipality: 'cityOrMunicipality',
  postalCode: 'postalCode',
  barangay: 'barangay',
};

type CurrentAddressProps = {
  initialValues?: Partial<CurrentAddressFormValues>;
  onBack?: () => void;
  onSubmit: (values: CurrentAddressFormValues) => void;
};

export const CurrentAddress = ({
  initialValues,
  onBack,
  onSubmit,
}: CurrentAddressProps) => {
  const methods = useForm<CurrentAddressFormValues>({
    resolver: zodResolver(currentAddressSchema),
    mode: 'all',
    defaultValues: initialValues ?? {
      country: 'PHL',
      region: '',
      province: '',
      cityOrMunicipality: '',
      postalCode: '',
      barangay: '',
      streetAndHouse: '',
      unitAndFloor: '',
    },
  });

  return (
    <form
      onSubmit={methods.handleSubmit(onSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<PersonFormHeader onBack={onBack} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={1}
            totalSteps={4}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <FormProvider {...methods}>
          <div className="flex flex-col gap-3 py-6">
            <Typography.Title size="s">
              What&apos;s your present address?
            </Typography.Title>
            <Typography.Text size="m" color="secondary">
              Add the address where you&apos;re living now.
            </Typography.Text>
          </div>

          <div className="flex flex-col gap-3">
            <AddressSuggestions fieldNames={suggestionFieldNames} />

            <Controller
              name="streetAndHouse"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="House number, Street name"
                  labelPosition="inner"
                />
              )}
            />

            <Controller
              name="unitAndFloor"
              control={methods.control}
              render={(props) => (
                <TextField
                  {...props}
                  label="Unit/Apartment (if any)"
                  labelPosition="inner"
                />
              )}
            />
          </div>
        </FormProvider>
      </Screen>
    </form>
  );
};
